/**
 * Region → preferred-language suggestion.
 *
 * Called once on first visit (before any localStorage / server pref is set)
 * to guess what language the visitor would prefer based on their locality.
 *
 * Rules:
 *  1. Indian-state overrides win when the state has a strong regional language.
 *     E.g. Maharashtra → Marathi, Tamil Nadu → Tamil, Kerala → Malayalam.
 *  2. Otherwise fall through to the country's primary language.
 *  3. Return null for "no confident suggestion" — we default to English then.
 *
 * We only suggest languages that exist in our translation allow-list, so a
 * suggested language always resolves through the /api/i18n/translate call.
 */

// Indian-state → language. Only states with a widely-used regional language
// distinct from Hindi are listed; everything else falls through to `hi`.
const IN_STATE_LANG = {
  "maharashtra": "mr",
  "tamil nadu": "ta",
  "karnataka": "kn",
  "kerala": "ml",
  "andhra pradesh": "te",
  "telangana": "te",
  "west bengal": "bn",
  "gujarat": "gu",
  "punjab": "pa",
  "odisha": "or",  // Odia not yet supported; will fall through to hi
  "assam": "as",   // Assamese not yet supported; will fall through to hi
};

// Country → primary language. Only listed where the answer isn't English.
const COUNTRY_LANG = {
  "argentina": "es",
  "bolivia": "es",
  "brazil": "pt",
  "chile": "es",
  "china": "zh",
  "colombia": "es",
  "costa rica": "es",
  "cuba": "es",
  "dominican republic": "es",
  "ecuador": "es",
  "egypt": "ar",
  "france": "fr",
  "germany": "de",
  "guatemala": "es",
  "honduras": "es",
  "india": "hi",
  "indonesia": "id",
  "iran": "fa",
  "iraq": "ar",
  "israel": "he",
  "italy": "it",
  "japan": "ja",
  "jordan": "ar",
  "kenya": "sw",
  "korea": "ko",
  "kuwait": "ar",
  "lebanon": "ar",
  "libya": "ar",
  "mexico": "es",
  "morocco": "ar",
  "nicaragua": "es",
  "oman": "ar",
  "pakistan": "ur",
  "panama": "es",
  "paraguay": "es",
  "peru": "es",
  "portugal": "pt",
  "qatar": "ar",
  "russia": "ru",
  "saudi arabia": "ar",
  "south korea": "ko",
  "spain": "es",
  "sudan": "ar",
  "syria": "ar",
  "taiwan": "zh",
  "tanzania": "sw",
  "thailand": "th",
  "tunisia": "ar",
  "turkey": "tr",
  "uae": "ar",
  "united arab emirates": "ar",
  "uganda": "sw",
  "ukraine": "uk",
  "uruguay": "es",
  "venezuela": "es",
  "vietnam": "vi",
  "yemen": "ar",
};

// Languages our translation pipeline actually supports (matches
// LanguageChip.LANGS + backend _ALLOWED_LANGS).
const SUPPORTED = new Set([
  "en", "es", "fr", "de", "pt", "it", "ru", "hi", "bn", "ta", "te", "ml",
  "mr", "gu", "pa", "kn",
  "ur", "ar", "fa", "he", "zh", "ja", "ko", "th", "vi", "id", "tr", "sw",
  "uk",
]);

const NATIVE_NAMES = {
  en: "English", es: "Español", fr: "Français", de: "Deutsch",
  pt: "Português", it: "Italiano", ru: "Русский", hi: "हिन्दी", bn: "বাংলা",
  ta: "தமிழ்", te: "తెలుగు", ml: "മലയാളം", mr: "मराठी", gu: "ગુજરાતી",
  pa: "ਪੰਜਾਬੀ", kn: "ಕನ್ನಡ", ur: "اردو", ar: "العربية", fa: "فارسی",
  he: "עברית", zh: "中文", ja: "日本語", ko: "한국어", th: "ไทย",
  vi: "Tiếng Việt", id: "Bahasa Indonesia", tr: "Türkçe", sw: "Kiswahili",
  uk: "Українська",
};

/**
 * Suggest a preferred language for a user based on their locality.
 * @param {{country?: string, state?: string}} locality
 * @returns {string | null} ISO-639-1 code, or null if no confident suggestion.
 */
export function suggestLangForRegion(locality) {
  if (!locality) return null;
  const country = (locality.country || "").trim().toLowerCase();
  const state = (locality.state || "").trim().toLowerCase();

  // India state overrides first.
  if (country === "india" && state) {
    const stateLang = IN_STATE_LANG[state];
    if (stateLang && SUPPORTED.has(stateLang)) return stateLang;
  }

  // Country map.
  const countryLang = COUNTRY_LANG[country];
  if (countryLang && SUPPORTED.has(countryLang)) return countryLang;

  return null; // English is the implicit default; caller handles it.
}

/**
 * The native-script name for a given code, or the code itself as fallback.
 * Used by the suggestion banner so people see हिन्दी instead of "Hindi".
 */
export function nativeNameFor(code) {
  return NATIVE_NAMES[code] || code;
}
